import { StatusCodes } from "http-status-codes";
import unidecode from "unidecode";
import { IDLE_PROGRESS } from "../../stories/screens/ProfileScreen/constants";
import { getRootUrl } from "../../utils/utils";
import { type Error } from "../actions/errorStore";
import { datadogLogs } from "@datadog/browser-logs";

interface FetchOptions {
  signal: AbortSignal;
}

export enum FETCH_STATES {
  IDLE = "IDLE",
  LOADING = "LOADING",
  LOADED = "LOADED",
  FAILED = "FAILED",
}

const generateHeaders = function (): HeadersInit {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  const token = localStorage.getItem("token");
  if (token !== null && token !== "") {
    if (token.startsWith('"')) {
      try {
        const parsedToken = JSON.parse(token);
        if (parsedToken) {
          headers.Authorization = `Token ${parsedToken}`;
        }
      } catch {
        // Do Nothing
      }
    } else {
      headers.Authorization = `Token ${token}`;
    }
  }
  return headers;
};

export interface SignedUrlFormProps {
  fields: {
    AWSAccessKeyId: string;
    key: string;
    policy: string;
    signature: string;
  };
  url: string;
}

export const uploadFilePromise = async (
  file: File,
  signedUrl: string | SignedUrlFormProps,
  progressHandler?: (progress: number) => void,
  xhr = new XMLHttpRequest(),
  abortSignal?: AbortSignal,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (abortSignal?.aborted) {
      reject(new Error("Upload was aborted"));
      return;
    }

    if (typeof signedUrl === "string") {
      xhr.open("PUT", signedUrl, true);
    } else {
      xhr.open("POST", signedUrl.url, true);
    }

    if (progressHandler) {
      xhr.upload.addEventListener("progress", (ev) => {
        progressHandler((ev.loaded * 100.0) / ev.total || 100);
      });

      xhr.onabort = () => {
        progressHandler(IDLE_PROGRESS);
      };
    }

    if (abortSignal) {
      abortSignal.addEventListener("abort", () => {
        xhr.abort();
        reject(new Error("Upload was aborted"));
      });
    }

    xhr.onreadystatechange = () => {
      if (
        xhr.readyState === XMLHttpRequest.DONE &&
        [StatusCodes.OK, StatusCodes.NO_CONTENT].includes(xhr.status)
      ) {
        resolve();
      }

      if (xhr.readyState === XMLHttpRequest.DONE && xhr.status >= 400) {
        reject(xhr.statusText);
      }
    };
    xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
    const fileName = unidecode(file.name);
    xhr.setRequestHeader(
      "Content-Disposition",
      'attachment; filename="' + fileName + '"',
    );

    if (typeof signedUrl === "string") {
      xhr.send(file);
    } else {
      const formData = new FormData();
      Object.entries(signedUrl.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append("file", file);
      xhr.send(formData);
    }
  });
};

export const uploadFile = async (
  file: File,
  signedUrl: string,
  progressHandler?: (progress: number) => void,
) => {
  const xhr = new XMLHttpRequest();
  xhr.open("PUT", signedUrl, true);
  if (progressHandler) {
    xhr.upload.addEventListener("progress", (ev) => {
      progressHandler((ev.loaded * 100.0) / ev.total || 100);
    });
  }
  xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
  const fileName = unidecode(file.name);
  xhr.setRequestHeader(
    "Content-Disposition",
    'attachment; filename="' + fileName + '"',
  );
  xhr.send(file);
};

export const uploadFilePart = async (
  blob: Blob,
  signedUrl: string,
  progressHandler?: (progress: number) => void,
  abortSignal?: AbortSignal,
): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (abortSignal?.aborted) {
      reject(new Error("Upload was aborted"));
      return;
    }

    const request = new XMLHttpRequest();
    request.open("PUT", signedUrl, true);

    request.onreadystatechange = () => {
      if (
        request.readyState === XMLHttpRequest.DONE &&
        request.status === StatusCodes.OK
      ) {
        const etag = request.getResponseHeader("etag");
        resolve(etag!);
      }
    };

    if (progressHandler) {
      request.upload.addEventListener("progress", (ev) => {
        progressHandler(ev.loaded);
      });
    }

    if (abortSignal) {
      abortSignal.addEventListener("abort", () => {
        request.abort();
        reject(new Error("Upload was aborted"));
      });
    }

    request.send(blob);
  });
};

/**
 * use makeBackendGetCallWithJsonResponse instead,
 * unless there's a need to access non-json response information
 */
export const makeBackendGetCall = async function (
  route: string,
  urlParams: string,
  fetchOptions?: FetchOptions,
): Promise<Response> {
  const headers = generateHeaders();
  const response = await fetch(getRootUrl() + route + urlParams, {
    headers,
    method: "GET",
    ...fetchOptions,
  });

  if (response.status >= StatusCodes.INTERNAL_SERVER_ERROR) {
    datadogLogs.logger.info(`[${route}][GET] Internal Server Error`, {
      urlParams,
      response,
      fetchOptions,
    });
    throw response;
  }
  return response;
};

// use makeBackendPostCallWithJsonResponse instead,
// unless there's a need to access non-json response information
const makeBackendPostCall = async function (
  route: string,
  data: object,
  fetchOptions?: FetchOptions,
): Promise<Response> {
  const headers = generateHeaders();
  const body = JSON.stringify(data);
  const response = await fetch(getRootUrl() + route, {
    headers,
    body,
    method: "POST",
    ...fetchOptions,
  });

  if (response.status >= StatusCodes.INTERNAL_SERVER_ERROR) {
    datadogLogs.logger.info(`[${route}][POST] Internal Server Error`, {
      data,
      response,
      fetchOptions,
    });
    throw response;
  }
  return response;
};

const makeBackendPutCall = async function (
  route: string,
  data: object,
  fetchOptions?: FetchOptions,
): Promise<Response> {
  const headers = generateHeaders();
  const body = JSON.stringify(data);
  const response = await fetch(getRootUrl() + route, {
    headers,
    body,
    method: "PUT",
    ...fetchOptions,
  });

  if (response.status >= StatusCodes.INTERNAL_SERVER_ERROR) {
    datadogLogs.logger.info(`[${route}][PUT] Internal Server Error`, {
      data,
      response,
      fetchOptions,
    });
    throw response;
  }
  return response;
};

const makeBackendDeleteCall = async function (
  route: string,
  fetchOptions?: FetchOptions,
): Promise<Response> {
  const headers = generateHeaders();
  const response = await fetch(getRootUrl() + route, {
    headers,
    method: "DELETE",
    ...fetchOptions,
  });

  if (response.status >= StatusCodes.INTERNAL_SERVER_ERROR) {
    datadogLogs.logger.info(`[${route}][DELETE] Internal Server Error`, {
      response,
      fetchOptions,
    });
    throw response;
  }
  return response;
};

export interface BackendCallWithJsonSuccessResponse<T> {
  success: true;
  resultJson: T;
  statusCode: number;
}

export interface BackendCallWithJsonErrorResponse {
  success: false;
  resultJson: Error;
  statusCode: number;
}

export const makeBackendGetCallWithJsonResponse = async <T>(
  route: string,
  urlParams: string,
  fetchOptions?: FetchOptions,
): Promise<
  BackendCallWithJsonSuccessResponse<T> | BackendCallWithJsonErrorResponse
> => {
  const response = await makeBackendGetCall(route, urlParams, fetchOptions);
  if (!response.ok) {
    return {
      statusCode: response.status,
      success: false,
      resultJson: (await response.json()) as Error,
    };
  }
  if (response.status === StatusCodes.NO_CONTENT) {
    return {
      statusCode: response.status,
      success: true,
      resultJson: {} as T,
    };
  }
  return {
    statusCode: response.status,
    success: true,
    resultJson: (await response.json()) as T,
  };
};

export const makeBackendPostCallWithJsonResponse = async <T>(
  route: string,
  data: object,
  fetchOptions?: FetchOptions,
): Promise<
  BackendCallWithJsonSuccessResponse<T> | BackendCallWithJsonErrorResponse
> => {
  const response = await makeBackendPostCall(route, data, fetchOptions);

  if (response.ok) {
    if (response.status === StatusCodes.NO_CONTENT) {
      return {
        statusCode: response.status,
        success: true,
        resultJson: {} as T,
      };
    }

    return {
      success: true,
      resultJson: (await response.json()) as T,
      statusCode: response.status,
    };
  }

  return {
    success: false,
    resultJson: (await response.json()) as Error,
    statusCode: response.status,
  };
};

export const makeBackendPutCallWithJsonResponse = async <T>(
  route: string,
  data: object,
  fetchOptions?: FetchOptions,
): Promise<
  BackendCallWithJsonSuccessResponse<T> | BackendCallWithJsonErrorResponse
> => {
  const response = await makeBackendPutCall(route, data, fetchOptions);

  if (response.status === StatusCodes.NO_CONTENT) {
    return {
      success: true,
      resultJson: {} as T,
      statusCode: response.status,
    };
  }

  if (response.ok) {
    return {
      success: true,
      resultJson: (await response.json()) as T,
      statusCode: response.status,
    };
  }

  return {
    success: false,
    resultJson: (await response.json()) as Error,
    statusCode: response.status,
  };
};

export const makeBackendDeleteCallWithJsonResponse = async <T>(
  route: string,
  fetchOptions?: FetchOptions,
): Promise<
  BackendCallWithJsonSuccessResponse<T> | BackendCallWithJsonErrorResponse
> => {
  const response = await makeBackendDeleteCall(route, fetchOptions);
  if (response.status === StatusCodes.NO_CONTENT) {
    return {
      success: true,
      resultJson: {} as T,
      statusCode: response.status,
    };
  }
  if (response.ok) {
    return {
      success: true,
      resultJson: (await response.json()) as T,
      statusCode: response.status,
    };
  }

  return {
    success: false,
    resultJson: (await response.json()) as Error,
    statusCode: response.status,
  };
};

export const readStream = async (
  route: string,
  progressHandler?: (progress: number) => void,
  knownContentType?: string, // this is an override for the content type in case the response header is blank
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    try {
      fetch(route, {
        method: "GET",
      })
        .then(async (response) => {
          if (response.status != StatusCodes.OK) {
            const errorJson = (await response.json()) as Error;
            reject(errorJson);
            return;
          }
          const reader: ReadableStreamDefaultReader<Uint8Array> | undefined =
            response.body?.getReader();
          if (!reader) throw response;

          const contentLength = response.headers.get("Content-Length");
          const contentType =
            knownContentType ?? response.headers.get("Content-Type");
          if (contentLength === null) throw response;
          const contentSize = +contentLength;
          let receivedLength = 0;
          const chunks = [];

          while (true) {
            const { done, value } = await reader.read();
            if (!value) break;
            if (done) break;
            chunks.push(value);
            receivedLength += value.length;
            if (progressHandler) progressHandler(receivedLength / contentSize);
          }
          resolve(
            new Blob(chunks, {
              type: contentType ?? undefined,
            }),
          );
        })
        .catch((e) => {
          reject(e);
        });
    } catch (e) {
      reject(e);
    }
  });
};
